import React, { useState, useEffect } from "react";
import { Table, Button, Typography, Modal, message, Spin } from "antd";
import { fetchAdminList, removeAdmin } from "../useAPIDetails/useRoles"; // Import API function
import { ReactComponent as VerifiedIcon } from "../../../Assets/images/icon/verifiedIcon.svg";
const { Title } = Typography;

const Roles = () => {
  const [admins, setAdmins] = useState([]); // Admin data state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
    total: 0,
  }); // Pagination state
  const [loading, setLoading] = useState(false); // Loading state
  const [isModalVisible, setIsModalVisible] = useState(false); // Modal visibility state
  const [selectedAdmin, setSelectedAdmin] = useState(null); // Store selected admin for removal

  // Load data into table
  const loadData = async (page, size) => {
    setLoading(true);
    try {
      const result = await fetchAdminList(page, size);
      const adminData = result.data.userProfiles?.map((user) => ({
        key: user?.id,
        userImage: user?.profilePicture || "", // Replace with image URL if available
        userName: `${user.firstName || "-"} ${user.lastName || "-"}`,
        email: user.email || "N/A",
        role: "Admin", // Assuming all are Admin
        verified: user?.verified || false,
      }));
      setAdmins(adminData);
      setPagination({
        current: page,
        pageSize: size,
        total: result?.data?.pagination?.totalElements,
      });
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load admin list."
      );
      console.error("Error fetching admin data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    loadData(pagination.current, pagination.pageSize);
  }, []);

  // Handle table pagination
  const handleTableChange = (pagination) => {
    loadData(pagination.current, pagination.pageSize);
  };

  // Show Modal and set selected Admin
  const showRemoveAdminModal = (record) => {
    setSelectedAdmin(record);
    setIsModalVisible(true);
  };

  // Handle Modal "Yes" Click
  const handleConfirmRemoveAdmin = async () => {
    if (selectedAdmin) {
      try {
        await removeAdmin(selectedAdmin.key);
        setAdmins((prev) =>
          prev.filter((admin) => admin.key !== selectedAdmin?.key)
        );
        message.success(`${selectedAdmin.userName} has been removed as Admin.`);
        setIsModalVisible(false);
        setSelectedAdmin(null);
      } catch (error) {
        console.error(error);
        message.error(
          error?.response?.data?.message || "Failed to promote user to admin"
        );
      }
    }
  };

  // Handle Modal "Cancel" Click
  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedAdmin(null);
  };

  // Define table columns
  const columns = [
    {
      title: "Profile Picture",
      dataIndex: "userImage",
      key: "userImage",
      render: (image) =>
        image ? (
          <img
            src={image}
            alt="Profile"
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
            }}
          />
        ) : (
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              backgroundColor: "#ccc",
            }}
          />
        ),
    },
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            gap: "8px",
          }}
        >
          <span>{text}</span>
          {record.verified && (
            <VerifiedIcon style={{ width: "30px", height: "30px" }} />
          )}
        </div>
      ),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          danger
          onClick={() => showRemoveAdminModal(record)}
        >
          Remove Admin
        </Button>
      ),
    },
  ];

  return (
    <div className="flex-grow-content" style={{ padding: "24px" }}>
      <Title level={4}>Admin Roles</Title>
      <Spin spinning={loading} tip="Loading Admins...">
        <Table
          columns={columns}
          dataSource={admins}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          onChange={handleTableChange}
          style={{
            border: "1px solid #E1E1E1",
            borderRadius: "5px",
            cursor: "pointer",
          }}
        />
      </Spin>

      {/* Remove Admin Modal */}
      <Modal
        title="Confirm Remove Admin"
        open={isModalVisible}
        onOk={handleConfirmRemoveAdmin}
        onCancel={handleCancel}
        centered
        footer={[
          <Button
            className="primary-button"
            variant="solid"
            onClick={handleCancel}
          >
            No
          </Button>,
          <Button className="primary-button" onClick={handleConfirmRemoveAdmin}>
            Yes
          </Button>,
        ]}
      >
        <p>
          Are you sure you want to remove{" "}
          <strong>{selectedAdmin?.userName}</strong> as Admin?
        </p>
      </Modal>
    </div>
  );
};

export default Roles;
